import type { NodeApi } from "@hogartrabe/react-arborist-vrh";
import { UpdateNodeSchema, type Node } from "interfaces/schemas";
import type { Project } from "../projectStore";
import type { NodeRequestItem } from "./utils/createNodeInput";
import * as treeUtils from "utils/treeUtils";
import { NodeKinds } from "interfaces/enums/nodeKinds";
import { processNodes } from "./utils/mutations";

export const updateNode = (
  node: NodeApi<Node>,
  args: Partial<Node>,
  tree: Node[],
  project: Project,
  language: string,
) => {
  if (!project?.id) {
    throw new Error("Project ID is missing. Cannot update node.");
  }
  const result = UpdateNodeSchema.safeParse({
    id: node.id,
    kind: node.data.kind,
    ...args,
  });
  if (!result.success) {
    throw new Error(
      result.error.message || "Failed to validate node update data",
    );
  }
  if (!treeUtils.validateNodeId(result.data.id, project.id)) {
    throw new Error("Invalid node ID");
  }
  const nodeData = treeUtils.findNodeById({
    nodes: tree,
    id: result.data.id,
  });

  if (!nodeData) {
    throw new Error("Node not found");
  }

  const updatedChildren: NodeRequestItem[] = [];

  const update: Partial<Node> = result.data;

  if (update.kind && update.kind !== nodeData.kind) {
    throw new Error("Node kind cannot be changed");
  }

  const oldQuantity = "quantity" in nodeData ? nodeData.quantity : undefined;

  if (
    "quantity" in update &&
    update.quantity !== undefined &&
    oldQuantity !== undefined
  ) {
    const newQuantity = update.quantity;
    if (
      nodeData.kind === NodeKinds.PRODUCT &&
      oldQuantity !== newQuantity &&
      nodeData.nodes &&
      nodeData.nodes.length > 0
    ) {
      const quantityRatio = newQuantity / oldQuantity;
      nodeData.nodes.forEach((child) => {
        const childNode = child as Node;
        if (childNode.kind === NodeKinds.PRODUCT_ACCESSORY) {
          childNode.quantity = Math.round(childNode.quantity * quantityRatio);
          updatedChildren.push({
            id: childNode.id,
            kind: childNode.kind,
            ...("quantity" in childNode && childNode.quantity !== undefined
              ? { quantity: childNode.quantity }
              : {}),
          });
        }
      });
    }
  }

  Object.assign(nodeData, update);
  node.data = nodeData;

  const nodesToUpdate: NodeRequestItem[] = [
    ...updatedChildren,
    {
      ...result.data,
      id: result.data.id,
    },
  ];

  processNodes(
    [
      {
        action: "UPDATE",
        nodes: nodesToUpdate,
      },
    ],
    project,
    language,
  );
  return [...tree];
};
